/**
 * useScreenState Hook
 *
 * Core state management hook for screen lifecycle.
 * Provides standardized loading, success, error, and empty states.
 *
 * @example
 * ```typescript
 * const screen = useScreenState({
 *   metadata: { screenType: ScreenType.DASHBOARD, title: 'Dashboard', requiresAuth: true },
 *   lifecycle: {
 *     onMount: async () => {
 *       screen.setLoading();
 *       const data = await fetchDashboardData();
 *       if (data) {
 *         screen.setSuccess();
 *       } else {
 *         screen.setEmpty();
 *       }
 *     }
 *   }
 * });
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '@/utils/logger';
import {
  ScreenState,
  ScreenStateHook,
  ScreenError,
  ScreenMetadata,
  ScreenLifecycle,
  LoadStrategy,
} from '@/types/screen.types';
import { ResponseCode } from '@/business/types/response-codes.types';

export interface UseScreenStateOptions {
  /** Screen metadata */
  metadata: ScreenMetadata;
  /** Screen lifecycle callbacks */
  lifecycle?: ScreenLifecycle;
  /**
   * REQUIRED: Screen's load implementation.
   * This method is called on mount and can be called anytime via screen.load()
   *
   * @param strategy Loading strategy (cache-first or API-first)
   * @param screenState Helper object to set screen state during load
   */
  onLoad: (
    strategy: LoadStrategy,
    screenState: {
      setLoading: () => void;
      setSuccess: () => void;
      setError: (error: ScreenError) => void;
      setEmpty: () => void;
      setIdle: () => void;
      setProgress: (value: number) => void;
    }
  ) => Promise<void>;
  /**
   * Default loading strategy for this screen.
   * Defaults to API_FIRST if not specified.
   */
  defaultStrategy?: LoadStrategy;
}

/**
 * Custom hook for managing screen state lifecycle.
 *
 * Features:
 * - Automatic state transitions
 * - Lifecycle callbacks (onMount, onUnmount, onError)
 * - Progress tracking
 * - Retry functionality
 * - Logger integration
 *
 * @param options Screen configuration options
 * @returns Screen state and control functions
 */
export function useScreenState(
  options: UseScreenStateOptions
): ScreenStateHook {
  const {
    metadata,
    lifecycle,
    onLoad,
    defaultStrategy = LoadStrategy.API_FIRST,
  } = options;

  // State
  const [state, setState] = useState<ScreenState>(ScreenState.INITIALIZING);
  const [error, setErrorState] = useState<ScreenError | null>(null);
  const [progress, setProgress] = useState(0);

  // Refs to track mounted state and last strategy used
  const isMounted = useRef(true);
  const lastStrategyRef = useRef<LoadStrategy>(defaultStrategy);
  // Deduplicates concurrent loads — React Strict Mode re-runs mount effects on the same
  // component instance, which would fire two API calls. This guard ensures only the first
  // invocation proceeds; the ref resets to false once the load settles.
  const loadInFlightRef = useRef(false);

  // Log state transitions
  const logStateChange = useCallback(
    (newState: ScreenState, context?: Record<string, unknown>) => {
      logger.debug(`Screen state: ${newState}`, {
        screen: metadata.title,
        screenType: metadata.screenType,
        previousState: state,
        ...context,
      });
    },
    [metadata, state]
  );

  // State transition functions
  const setLoading = useCallback(() => {
    setState(ScreenState.LOADING);
    setErrorState(null);
    setProgress(0);
    logStateChange(ScreenState.LOADING);
  }, [logStateChange]);

  const setSuccess = useCallback(() => {
    setState(ScreenState.SUCCESS);
    setErrorState(null);
    setProgress(100);
    logStateChange(ScreenState.SUCCESS);
  }, [logStateChange]);

  const setError = useCallback(
    (errorObj: ScreenError) => {
      setState(ScreenState.ERROR);
      setErrorState(errorObj);
      setProgress(0);
      logStateChange(ScreenState.ERROR, { code: errorObj.code });

      // Call lifecycle error handlers
      if (lifecycle?.onError) {
        lifecycle.onError(errorObj);
      }
      if (lifecycle?.onLoadError) {
        lifecycle.onLoadError(errorObj);
      }
    },
    [lifecycle, logStateChange]
  );

  const setEmpty = useCallback(() => {
    setState(ScreenState.EMPTY);
    setErrorState(null);
    setProgress(100);
    logStateChange(ScreenState.EMPTY);
  }, [logStateChange]);

  const setIdle = useCallback(() => {
    setState(ScreenState.IDLE);
    setErrorState(null);
    setProgress(0);
    logStateChange(ScreenState.IDLE);
  }, [logStateChange]);

  const setInProgress = useCallback(
    (progressValue = 50) => {
      setState(ScreenState.IN_PROGRESS);
      setProgress(progressValue);
      logStateChange(ScreenState.IN_PROGRESS, { progress: progressValue });
    },
    [logStateChange]
  );

  // Load screen data (REQUIRED method - every screen must implement)
  const load = useCallback(
    async (strategy: LoadStrategy = defaultStrategy) => {
      logger.info('Loading screen data', {
        screen: metadata.title,
        strategy,
      });

      lastStrategyRef.current = strategy;
      setLoading();

      try {
        // Call the screen's load implementation with helper methods
        await onLoad(strategy, {
          setLoading,
          setSuccess,
          setError,
          setEmpty,
          setIdle,
          setProgress,
        });

        // Call onLoadSuccess if screen is in success state
        if (lifecycle?.onLoadSuccess) {
          lifecycle.onLoadSuccess();
        }
      } catch (err) {
        // If onLoad throws, treat it as an error
        logger.error('Load failed with exception', err, {
          screen: metadata.title,
          strategy,
        });

        const screenError: ScreenError = {
          code: ResponseCode.INTERNAL_ERROR,
          message:
            err instanceof Error ? err.message : 'Failed to load screen data',
          canRetry: true,
        };

        setError(screenError);
      }
    },
    [
      metadata,
      defaultStrategy,
      onLoad,
      lifecycle,
      setLoading,
      setSuccess,
      setError,
      setEmpty,
      setIdle,
      setProgress,
    ]
  );

  // Reload screen data (uses last strategy)
  const reload = useCallback(async () => {
    logger.info('Reloading screen data', {
      screen: metadata.title,
      strategy: lastStrategyRef.current,
    });
    await load(lastStrategyRef.current);
  }, [metadata, load]);

  // Reset to initial state
  const reset = useCallback(() => {
    setState(ScreenState.INITIALIZING);
    setErrorState(null);
    setProgress(0);
    lastStrategyRef.current = defaultStrategy;
    logStateChange(ScreenState.INITIALIZING);
  }, [logStateChange, defaultStrategy]);

  // Retry last failed action (calls load again)
  const retry = useCallback(async () => {
    logger.info('Retrying load', {
      screen: metadata.title,
      strategy: lastStrategyRef.current,
    });
    await load(lastStrategyRef.current);
  }, [metadata, load]);

  // Derived states
  const isLoading =
    state === ScreenState.LOADING || state === ScreenState.INITIALIZING;
  const isError = state === ScreenState.ERROR;
  const isSuccess = state === ScreenState.SUCCESS;
  const isEmpty = state === ScreenState.EMPTY;
  const isInProgress = state === ScreenState.IN_PROGRESS;

  // Lifecycle: onMount - automatically load screen data
  useEffect(() => {
    // Guard against React Strict Mode double-invocation. Strict Mode re-runs effects
    // on the same component instance (not a true remount), so the ref persists and
    // blocks the duplicate call. It resets to false once the async load settles.
    if (loadInFlightRef.current) return;
    loadInFlightRef.current = true;

    isMounted.current = true;

    const runOnMount = async () => {
      logger.info('Screen mounted', {
        screen: metadata.title,
        screenType: metadata.screenType,
      });

      // Automatically call load() on mount
      await load(defaultStrategy);

      // Call optional onMount callback after load completes
      if (lifecycle?.onMount) {
        try {
          const result = lifecycle.onMount();
          if (result instanceof Promise) {
            await result;
          }
        } catch (err) {
          logger.error('onMount callback failed', err, {
            screen: metadata.title,
          });
        }
      }
    };

    runOnMount().finally(() => {
      loadInFlightRef.current = false;
    });

    // Cleanup: onUnmount
    return () => {
      isMounted.current = false;

      if (lifecycle?.onUnmount) {
        logger.info('Screen unmounted', {
          screen: metadata.title,
          screenType: metadata.screenType,
        });
        lifecycle.onUnmount();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata.title, metadata.screenType]);
  // Note: We only want to run this once on mount, so we don't include all dependencies

  return {
    state,
    error,
    progress,
    setLoading,
    setSuccess,
    setError,
    setEmpty,
    setIdle,
    setInProgress,
    isLoading,
    isError,
    isSuccess,
    isEmpty,
    isInProgress,
    load,
    reload,
    reset,
    retry,
  };
}
